import { useState } from 'react';
import { Header, Section, Stepper } from '@components';
import { DefaultLayout } from '@layouts';
import CircularProgress from '@mui/material/CircularProgress';
import axios from 'axios';
import dayjs from 'dayjs';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { NextSeo } from 'next-seo';
import { stringify } from 'qs';
import { useTranslation } from 'next-i18next';

export default function BookingPage({ stores, data, type, store }) {
  const { header, seo } = data;
  const { t } = useTranslation();
  // Forms
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(null);

  const onSubmit = async (e, formValues) => {
    try {
      e?.preventDefault();
      e?.stopPropagation();

      setLoading(true);

      const toSend = { ...formValues };
      const selectedStore = stores?.find(
        ({ attributes }) => attributes?.slug === toSend?.store,
      );

      toSend.store = selectedStore?.attributes?.name;
      toSend.date = dayjs(toSend.date).format('DD-MM-YYYY');

      const requestBooking = await axios.post(
        process.env.NEXT_PUBLIC_BOOKING,
        toSend,
      );

      setResponse(requestBooking?.data);
      setLoading(false);
    } catch (error) {
      console.error('onSubmit:booking', error);
    }
  };
  return (
    <>
      <NextSeo
        title={`Momento | Booking`}
        description={seo?.description}
      />
      <DefaultLayout showBookingCta={false}>
        <div className="flex flex-col ">
          <Section className={' pt-4 md:pt-8 xl:pt-12'}>
            {!response && !loading && (
              <Header {...header} centered />
            )}

            {loading && (
              <div className="flex items-center justify-center mx-auto">
                <CircularProgress />
              </div>
            )}

            {response && (
              <Header
                label={
                  response?.success
                    ? t('forms.response.label_success')
                    : t('forms.response.label_error')
                }
                title={
                  response?.success
                    ? t('pages.checkout.checkout_thank_you.title')
                    : t('pages.checkout.checkout_error.title')
                }
                description={
                  response?.success
                    ? t('forms.response.booking_success')
                    : t('forms.response.booking_error')
                }
                centered
              />
            )}
          </Section>
          <Section>
            {!response && !loading && (
              <Stepper
                store={store}
                type={type}
                stores={stores}
                onSubmit={onSubmit}
              />
            )}
          </Section>
        </div>
      </DefaultLayout>
    </>
  );
}

export async function getServerSideProps({
  res,
  locale,
  params,
  query,
}) {
  res.setHeader(
    'Cache-Control',
    'public, s-maxage=10, stale-while-revalidate=59',
  );

  const path = process.env.NEXT_PUBLIC_STRAPI;

  const qsBookingPage = stringify(
    { populate: ['seo', 'header', 'header.data'], locale },
    { encodeValuesOnly: true },
  );

  const today = dayjs();
  const targetDate = dayjs('2025-07-31');
  const closingPeriod =
    today.isBefore(targetDate) || today.isSame(targetDate);

  const excludedSlugs = ['ristorante-momento-neapolis'];
  if (closingPeriod) {
    excludedSlugs.push('ristorante-degli-scavi');
  }

  const filters =
    excludedSlugs.length > 0
      ? {
          slug: {
            $nin: excludedSlugs,
          },
        }
      : {};
  const qsStores = stringify(
    {
      populate: ['city.data', 'location.data'],
      sort: ['name'],
      locale,
      filters,
    },
    { encodeValuesOnly: true },
  );

  // HomePage Data
  const getBookingPage = await fetch(
    `${path}/booking-page?${qsBookingPage}`,
  );
  const bookingPage = await getBookingPage?.json();

  // Stores data
  const getStores = await fetch(`${path}/stores?${qsStores}`);
  const getStoresData = await getStores?.json();

  const allStores = getStoresData?.data || [];
  // Manually filter the results in JS to ensure exclusion, as API filtering might be tricky/cached.
  const filteredStores = allStores.filter(
    (store) => !excludedSlugs.includes(store?.attributes?.slug),
  );

  return {
    props: {
      ...(await serverSideTranslations(locale, ['common'])),
      stores: filteredStores,
      data: bookingPage?.data?.attributes || {},
      type: query?.type || null,
      store: query?.store || null,
    },
  };
}
